import type { Demo, Image, ToolMark, WorkChapter, WorkItem } from './schema'
import { site } from './site'
import { method } from './method'
import { tools } from './tools'
import { aiGroups, apps, bookingPages, buildMethod, challengeFunnel, sampleSites, ticketingSystem, workCount } from './work'

/** Home page copy. The hero pairs a thin line with a bold one (Kenneth's pick, 2026-10-01). */
export const homeHero = {
  headlineThin: 'Build it once.',
  headlineBold: 'Run it forever.',
  subhead: 'I help coaches and service brands turn every lead into a booked call, on autopilot.',
  cta: { label: 'Hire Me!', to: '/#contact' },
}

/**
 * The statement under the hero: words light up as the
 * page scrolls, each keyword lands bold and moves the tracker. Built from the hero sub-head and the
 * Brewed Method (Catch, Brew, Pour). [CONFIRM] the exact wording with Kenneth.
 * A string is plain words; `{ key }` is a keyword (the tracker shows them in order).
 */
export type ManifestoPart = string | { key: string }

export const homeManifesto = {
  eyebrow: method.eyebrow,
  parts: [
    'Leads need a',
    { key: 'system.' },
    'I help coaches and service brands',
    { key: 'catch' },
    'every lead,',
    { key: 'brew' },
    'the follow-up for as long as it takes, and',
    { key: 'pour' },
    'ready-to-buy people into the calendar.',
  ] satisfies ManifestoPart[],
}

export const homeSections = {
  work: {
    eyebrow: 'Selected work',
    title: 'Builds you can open right now.',
  },
  proof: {
    eyebrow: 'Proof',
    title: 'What I run for clients today.',
  },
  objections: {
    eyebrow: 'Before you write',
    title: 'The three questions everyone asks.',
  },
}

export const ctaBand = {
  title: 'Tell me what is eating your week.',
  body: 'If I can automate it, you get a written plan back. If I cannot, I will say so.',
  button: 'Email me your bottleneck',
}

/* ---------- Work section ---------- */

/** Full-size captures (scripts/capture-covers.mjs, capture-url.mjs), 1800x1125, with 480 / 640 / 960
 *  copies for the Work tiles (scripts/make-image-variants.py). */
const cover = (id: string, label: string): Image => ({
  src: `/images/covers/${id}.webp`,
  alt: `${label} page`,
  width: 1800,
  height: 1125,
  srcSet: [480, 640, 960].map((w) => `/images/covers/${w}/${id}.webp ${w}w`).join(', ') + `, /images/covers/${id}.webp 1800w`,
})
/** Phone-size captures for the slider cards (`--portrait`), 600x1020. */
const card = (id: string, label: string): Image => ({
  src: `/images/cards/${id}.webp`,
  alt: `${label}, phone view`,
  width: 600,
  height: 1020,
})
/** GoHighLevel screens from Kenneth's own HL Accelerator account (scripts/port-automations.py), with
 *  480 / 640 / 960 copies for the Work tile (scripts/make-image-variants.py). */
const screen = (id: string, alt: string, width: number, height: number): Image => ({
  src: `/images/automations/${id}.webp`,
  alt,
  width,
  height,
  srcSet: [480, 640, 960].map((w) => `/images/automations/${w}/${id}.webp ${w}w`).join(', ') + `, /images/automations/${id}.webp ${width}w`,
})

/** A live demo page as a showcase item, with its full-size cover. */
const demoItem =
  (group: string) =>
  (demo: Demo): WorkItem => ({
    id: demo.id,
    name: demo.label,
    kicker: demo.tag,
    group,
    summary: demo.summary,
    image: cover(demo.id, demo.label),
    href: demo.href,
  })

const aiItems = (titles: string[]): WorkItem[] =>
  aiGroups
    .filter((group) => titles.includes(group.title))
    .flatMap((group) =>
      group.builds.map((build) => ({
        id: build.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        name: build.name,
        kicker: group.title,
        summary: build.summary,
        stack: build.stack,
        status: build.status,
      })),
    )

/** The BrewedOps mark for the Chatbots card (they live on brewedops.com). The card ground is always light. */
const brewedOpsMark: ToolMark = { name: site.brand, logo: site.logo.light }

/** The URL of a chapter's showcase sheet. Real, shareable and in the sitemap. */
export const chapterPath = (id: string) => `/work/${id}`

export const workSection = {
  ...homeSections.work,
  /** The title as one display line in the hero's thin + bold pairing. */
  titleThin: 'Builds you can',
  titleBold: 'open right now.',
  all: { label: `See all ${workCount} builds`, to: '/work' },
}

const automationItems: WorkItem[] = [
  { id: 'lead-qualifier', name: 'Lead Qualifier and New Lead', kicker: 'Workflow', summary: 'A form comes in, the budget answer splits the path, and the opportunity is found or created.', image: screen('lead-qualifier', 'Lead qualifier workflow in the GoHighLevel builder', 1678, 919) },
  { id: 'booked-call', name: 'Booked Call', kicker: 'Workflow', summary: 'A booked discovery call swaps the tags, updates the opportunity and waits for the call.', image: screen('booked-call', 'Booked call workflow in the GoHighLevel builder', 1521, 913) },
  { id: 'call-outcome-proposal', name: 'Call Outcome: Proposal', kicker: 'Workflow', summary: 'Moving the deal to Proposal tags the contact, sends the proposal email and starts the wait.', image: screen('call-outcome-proposal', 'Proposal workflow in the GoHighLevel builder', 1648, 915) },
  { id: 'workflow-list', name: 'The workflow set', kicker: 'Workflows', summary: 'Lead in, booked call, follow-up, proposal, no-show and closed-won, published as one set.', image: screen('workflow-list', 'The full list of workflows in GoHighLevel', 1177, 809) },
  { id: 'custom-fields', name: 'Custom fields', kicker: 'CRM setup', summary: 'The fields the forms and workflows read: call purpose, timelines, contact preferences.', image: screen('custom-fields', 'Custom fields in GoHighLevel settings', 1617, 825) },
  { id: 'tags', name: 'Tags', kicker: 'CRM setup', summary: 'The tags that move contacts through the pipeline and trigger each workflow.', image: screen('tags', 'Tags in GoHighLevel', 1490, 838) },
]

/**
 * The Work chapters, in slider order (Kenneth's list, 2026-10-01). Add, remove or reorder a chapter
 * here; the slider, the sheets and the sitemap follow. `card` is the slider picture; a chapter without
 * one shows its build names on the card instead.
 */
export const workChapters: WorkChapter[] = [
  {
    id: 'ticketing',
    title: 'Ticketing System',
    line: 'A full support desk built inside GoHighLevel.',
    count: 1,
    unit: 'blueprint',
    description: ticketingSystem.summary,
    card: card('ticketing-blueprint', 'Support Ticketing System blueprint'),
    marks: [tools.ghl, brewedOpsMark],
    items: [],
    lead: cover('ticketing-blueprint', 'Support Ticketing System blueprint'),
    gallery: ticketingSystem.gallery,
    link: ticketingSystem.link,
  },
  {
    id: 'agentic-framework',
    title: 'Agentic Framework',
    line: 'The tools, the rules and the loop I build with.',
    count: buildMethod.length,
    unit: 'pages',
    description: 'The tools, the rules, and the loop I use to ship apps with AI, and every step of it wired as one live graph.',
    card: card('agentic-framework', 'The Agentic Framework'),
    marks: [tools.github, tools.claudeCode, tools.claude],
    items: buildMethod.map((page, i) => ({
      id: i === 0 ? 'framework' : 'workflow',
      name: page.title,
      kicker: 'Live page',
      summary: page.summary,
      image: cover(i === 0 ? 'agentic-framework' : 'workflow-graph', page.title),
      href: page.link.href,
    })),
  },
  {
    id: 'build-plan',
    title: 'Build Plan',
    line: 'A real automation plan, before a workflow is clicked together.',
    count: 1,
    unit: 'plan',
    description: 'A real GoHighLevel automation plan: every product, trigger, tag and email step mapped before a workflow is clicked together.',
    card: card('build-plan', 'Sample automation plan'),
    marks: [tools.ghl],
    items: [],
    lead: cover('build-plan', 'Sample automation plan'),
    link: { label: 'Open the sample plan', href: '/demos/plans/sample-automation-plan.html', external: true },
  },
  {
    id: 'automations',
    title: 'Automations',
    line: 'Real screens from the GoHighLevel workflows I build.',
    count: automationItems.length,
    unit: 'screens',
    description: 'Real screens from GoHighLevel workflows I built: lead qualifying, booked calls, proposals, and the fields and tags behind them.',
    card: card('automations', 'Lead qualifier workflow'),
    marks: [tools.n8n, tools.ghl, tools.openai],
    items: automationItems,
  },
  {
    id: 'websites',
    title: 'Websites',
    line: 'Sample sites, a full challenge funnel and booking pages.',
    count: sampleSites.length + challengeFunnel.length + bookingPages.length,
    unit: 'pages',
    description: 'Full sample websites for local businesses, every page of a 7-day challenge funnel, and booking pages. Open each one live.',
    card: card('solar-helios', 'Commercial Solar sample site'),
    marks: [tools.vite, tools.react, tools.cloudflare],
    items: [
      ...sampleSites.map(demoItem('Sample sites')),
      ...challengeFunnel.map(demoItem('Challenge funnel')),
      ...bookingPages.map(demoItem('Booking pages')),
    ],
  },
  {
    id: 'apps',
    title: 'Apps and Extensions',
    line: 'Mobile apps and the Chrome tools I ship alongside them.',
    count: apps.length,
    unit: 'apps',
    description: 'Android apps and Chrome extensions I build and ship: Pickles, Celery Music, Lettuce Read, BrewedOps GHL Key and BrewedYT2Text.',
    card: { src: '/images/apps/pickles.webp', alt: 'Pickles on the Google Play Store', width: 540, height: 1200 },
    // Kenneth, 2026-10-03: the tile shows Pickles, not Celery. The store shot from the "Google Play" sheet down.
    tile: {
      src: '/images/apps/pickles-tile.webp',
      alt: 'Pickles on the Google Play Store',
      width: 540,
      height: 928,
      srcSet: '/images/apps/320/pickles-tile.webp 320w, /images/apps/pickles-tile.webp 540w',
    },
    marks: [tools.expo, tools.claudeCode, tools.chrome],
    items: apps.map((app) => ({ id: app.id, name: app.name, kicker: app.kind, summary: app.summary, image: app.image, status: app.status })),
  },
  {
    id: 'ai-rag',
    title: 'AI Agents and RAG',
    line: 'Task agents and retrieval chatbots wired to real data.',
    count: aiItems(['Agents', 'RAG chatbots']).length,
    unit: 'builds',
    description: 'Task agents that pick their own tools, and retrieval (RAG) chatbots that search real documents before they answer.',
    marks: [tools.hermes, tools.claude, tools.claudeCode],
    items: aiItems(['Agents', 'RAG chatbots']),
  },
  {
    id: 'chatbots',
    title: 'Chatbots',
    line: 'Chatbots on BrewedOps.com, each doing one job well.',
    count: aiItems(['Chatbots on BrewedOps.com']).length,
    unit: 'bots',
    description: 'The chatbots inside BrewedOps.com: a GoHighLevel workflow creator, a voice call tester and a website copier.',
    marks: [brewedOpsMark, tools.claude],
    items: aiItems(['Chatbots on BrewedOps.com']),
  },
]

export const findChapter = (id: string | undefined) => workChapters.find((chapter) => chapter.id === id)
